/* eslint-disable @typescript-eslint/no-explicit-any */
import { AuthorIcon } from '@/components/Icon/AuthorIcon'
import ImageOptimize from '@/components/ImageOptimize'
import PageBreadcrumb from '@/components/PageBreadcrumb'
import { PageTypeSuffix } from '@/constants'
import { useNotification } from '@/containers'
import { Meta } from '@/layouts/Meta'
import { Main } from '@/templates/Main'
import { type Post } from '@/types'
import { getTranslate, setContext } from '@/utils/api-interceptors'
import {
  getBannerSSR,
  getBottomMenu,
  getConfigSSR,
  getMenus,
  getPostDetailSSR,
  getQuickMenu,
  getSaleRegion,
} from '@/utils/endpoint'
import * as pixel from '@/utils/fpixel'
import { getCookies } from 'cookies-next'
import { type GetServerSideProps, type GetServerSidePropsContext } from 'next'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

const PostDetail = ({
  postDetail,
  hideMenu,
}: {
  postDetail: Post
  hideMenu: string
}) => {
  const breadcrumbs = [{ label: 'Thông tin hữu ích', href: '/post' }]
  const t = useTranslations('Common')
  const router = useRouter()
  const { systemConfig } = useNotification()
  useEffect(() => {
    pixel.fbqEvent('ViewContent')
  }, [])
  if (hideMenu) {
    return (
      <div className='postDetailContent mb-[40px] mt-[32px]'>
        <div className='mb-[32px] mt-[36px] font-bold italic text-dark-400'>
          {getTranslate(postDetail?.description)}
        </div>
        <div
          className='main-description break-words'
          dangerouslySetInnerHTML={{
            __html: getTranslate(postDetail?.content),
          }}
        ></div>
      </div>
    )
  }

  return (
    <Main
      meta={
        <Meta
          title={
            getTranslate(postDetail?.metaTitle) &&
            getTranslate(postDetail?.metaTitle) !== ''
              ? getTranslate(postDetail?.metaTitle)
              : getTranslate(postDetail?.title)
          }
          description={
            getTranslate(postDetail?.metaDescription) &&
            getTranslate(postDetail?.metaDescription) !== ''
              ? getTranslate(postDetail?.metaDescription)
              : getTranslate(postDetail?.description)
          }
          noFollow={postDetail?.noFollow}
          noIndex={postDetail?.noIndex}
          imageUrl={
            postDetail?.images[0] ||
            systemConfig?.imagePlaceholder ||
            process.env.NEXT_PUBLIC_IMAGE_PLACEHOLDER ||
            ''
          }
          canonical={
            postDetail?.canonicalUrl ||
            process.env.NEXT_PUBLIC_URL_WEBSITE + router.asPath
          }
          mobileAlternate={{
            media: 'only screen and (max-width:991px))',
            href:
              process.env.NEXT_PUBLIC_URL_WEBSITE_MOBILE + router.asPath ||
              process.env.NEXT_PUBLIC_URL_WEBSITE_MOBILE ||
              '',
          }}
        />
      }
    >
      <section className='postDetail min-h-[500px] pb-10'>
        <PageBreadcrumb
          homeText={t('home')}
          breadcrumbs={breadcrumbs}
          className='mb-[20px]'
        />
        <div className='mt-2 flex'>
          <div className='flex-2 pr-[70px]'>
            <h1 className='text-titleSection text-dark-400'>
              {getTranslate(postDetail?.title)}
            </h1>
            <div className='postDetailHeader mt-2 flex'>
              <div className='mr-[24px] flex items-center'>
                <AuthorIcon className='mr-[8px]' />
                <span>{systemConfig?.authorDefault || 'Quản trị viên'}</span>
              </div>
            </div>
            <div className='postDetailContent mb-[40px] mt-[32px]'>
              <div className='mb-[32px] mt-[36px] font-bold italic text-dark-400'>
                {getTranslate(postDetail?.description)}
              </div>
              <div
                className='main-description break-words'
                dangerouslySetInnerHTML={{
                  __html: getTranslate(postDetail?.content),
                }}
              ></div>
            </div>
          </div>
          <div className='sideBar  max-w-[25%] flex-4'>
            <div className='sticky top-[20px]'>
              <h3 className='text-HeadlinePage font-bold text-dark-400'>
                Bài viết liên quan
              </h3>
              <div className='mt-2'>
                {postDetail?.relatedPosts.length > 0 &&
                  postDetail?.relatedPosts.map((e, index) => (
                    <Link
                      key={index}
                      href={`/${getTranslate(e.slugs.value)}${e.slugs.postfix}${
                        PageTypeSuffix.POST
                      }`}
                    >
                      <div className='mb-[24px] flex items-center gap-x-[20px]'>
                        <ImageOptimize
                          className='cartImage h-[88px] w-[88px] object-cover'
                          src={
                            e.images[0] ||
                            systemConfig?.imagePlaceholder ||
                            process.env.NEXT_PUBLIC_IMAGE_PLACEHOLDER ||
                            ''
                          }
                          loading='lazy'
                          alt={getTranslate(e.title) || ''}
                          width={88}
                          height={88}
                        />
                        <p>{getTranslate(e.title)}</p>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Main>
  )
}

export default PostDetail

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { req, res, query } = context
  const { token, language, messages } = await setContext(
    getCookies({ req, res }),
    {
      language: context.locale,
    }
  )

  const { slug, hideMenu } = query
  if (!slug) {
    return {
      notFound: true,
    }
  }

  const [
    menus,
    regions,
    banner,
    quickMenu,
    postDetail,
    bottomMenu,
    systemConfig,
  ] = await Promise.all([
    getMenus(),
    getSaleRegion(),
    getBannerSSR(),
    getQuickMenu(),
    getPostDetailSSR((slug as string).replace('.b', '')),
    getBottomMenu(),
    getConfigSSR(),
  ])
  if (!postDetail) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      menus,
      regions,
      banner,
      language,
      messages,
      quickMenu,
      bottomMenu,
      systemConfig,
      postDetail,
      hideMenu: hideMenu || null,
      token: token || null,
    },
  }
}
